'use client';

import type { ReactNode } from 'react';
import React from 'react';
import { Button as AriaButton, type ButtonProps as AriaButtonProps } from 'react-aria-components';
import { Tooltip, type TooltipProps } from '../tooltip';
import { cx, sortCx } from '../../../utils/cx';

const SEMANTIC_NAME = 'RomaUtilityButton';

export const styles = sortCx({
    common: {
        icon: 'shrink-0 transition-inherit-all',
        root: [
            'group relative inline-flex shrink-0 cursor-pointer items-center justify-center rounded-sm whitespace-nowrap select-none outline-primary transition duration-100 ease-linear before:absolute focus-visible:outline-2 focus-visible:-outline-offset-1',
            // Disabled styles
            'disabled:cursor-default disabled:text-fg-disabled',
            // Hover styles
            'hover:bg-primary_alpha',
        ].join(' '),
        // Selected state is shown with a black selection ring or a background fill, depending on `selectionStyle`.
        selected: {
            ring: 'ring-2 ring-focus-ring',
            background: 'bg-primary_alpha',
        },
    },
    sizes: {
        xs: { root: 'size-7', icon: 'size-4' },
        sm: { root: 'size-8', icon: 'size-5' },
    },
});

export type UtilityButtonIndicator = { type: 'dot' } | { type: 'badge'; count: number };

export type UtilityButtonProps = Omit<AriaButtonProps, 'children'> & {
    /** Icon-only content for the utility button. */
    children: ReactNode;
    /** Optional visual status treatment. Consumers provide accessible status copy. */
    indicator?: UtilityButtonIndicator;
    /** Tooltip props displayed on hover or focus. */
    tooltip?: Omit<TooltipProps, 'children'>;
    /** The size variant of the button. */
    size?: 'xs' | 'sm';
    /** Shows the selected state. */
    isSelected?: boolean;
    /** How the selected state is shown: a 2px selection ring (default) or a background fill. */
    selectionStyle?: keyof typeof styles.common.selected;
};

/** Renders a persistent notification dot with a reduced-motion-safe halo. */
const Dot: React.FC = () => (
    <span className="pointer-events-none absolute top-xxs right-xxs flex size-2" aria-hidden data-utility-button-dot>
        <span className="absolute size-full rounded-full bg-brand-solid opacity-75 motion-safe:animate-ping" />
        <span className="relative size-2 rounded-full bg-brand-solid" />
    </span>
);

/** Renders a count badge capped at the supported display maximum. */
const Badge: React.FC<{ count: number }> = ({ count }) => (
    <span
        className="pointer-events-none absolute -right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-xs bg-brand-solid px-1 text-xs leading-4 font-semibold text-primary_on-brand whitespace-nowrap"
        aria-hidden
        data-utility-button-badge
    >
        {count > 99 ? '99+' : count}
    </span>
);

/** Applies the size-specific utility button icon class while preserving custom icon classes. */
const renderUtilityIcon = (icon: ReactNode, iconClassName: string) => {
    if (!React.isValidElement<{ className?: string }>(icon)) return icon;

    return React.cloneElement(icon, {
        className: cx(styles.common.icon, iconClassName, icon.props.className),
    });
};

export const UtilityButton = React.forwardRef<HTMLButtonElement, UtilityButtonProps>(
    (
        {
            children,
            className,
            indicator,
            tooltip,
            size = 'sm',
            isDisabled,
            isSelected,
            selectionStyle = 'ring',
            'aria-label': ariaLabel,
            ...otherProps
        },
        ref,
    ) => {
        const button = (
            <AriaButton
                {...otherProps}
                ref={ref}
                aria-label={ariaLabel ?? (typeof tooltip?.title === 'string' ? tooltip.title : undefined)}
                data-icon-only
                data-selected={isSelected ? true : undefined}
                isDisabled={isDisabled}
                className={(state) =>
                    cx(
                        SEMANTIC_NAME,
                        styles.common.root,
                        // opts in to hit area expansion; inert unless a container sets --hit-slop-x/y
                        'hit-slop',
                        styles.sizes[size].root,
                        'text-fg-toolbar-primary',
                        isSelected && styles.common.selected[selectionStyle],
                        typeof className === 'function' ? className(state) : className,
                    )
                }
            >
                {renderUtilityIcon(children, styles.sizes[size].icon)}
                {indicator?.type === 'dot' && <Dot />}
                {indicator?.type === 'badge' && <Badge count={indicator.count} />}
            </AriaButton>
        );

        if (!tooltip) return button;

        return (
            <Tooltip {...tooltip} delay={tooltip.delay ?? 700}>
                {button}
            </Tooltip>
        );
    },
);

UtilityButton.displayName = SEMANTIC_NAME;
